import type { CSSProperties } from 'react';
import styled from 'styled-components';
import QR from '../atoms/QR/QR';
import useIsDesktop from '../../hooks/useIsDesktop';

/**
 * CPAYMENT-1718: клиент платит с телефона, а экран загрузки чека открывает на ПК —
 * чек остаётся в телефоне. Блок предлагает второй способ сделать ровно то же действие:
 * загрузить чек, но с телефона. QR ведёт на текущий URL — тот же тикет, тот же шаг.
 *
 * Отсюда и вёрстка: разделитель «или» отделяет блок от загрузчика выше, чтобы он читался
 * как альтернативная ветка того же действия, а не как ещё одна кнопка в ряду.
 * На узком экране (<= 540px) не рендерится вовсе — клиент уже на телефоне.
 */

export type OpenOnPhoneVariant = 'default' | 'lion' | 'pinco';

type Tokens = {
  surface: string;
  radius: string;
  title: string;
  hint: string;
  accent: string;
  divider: string;
  /** Подложка под самим кодом — всегда светлая, код должен считываться камерой. */
  frame: string;
  font: string;
};

// Палитры дублируют токены шаблонов намеренно: components/ не должен зависеть от templates/.
const TOKENS: Record<OpenOnPhoneVariant, Tokens> = {
  default: {
    surface: 'var(--fields-background-color)',
    radius: 'var(--border-radius)',
    title: 'var(--text-bold-color)',
    hint: 'var(--second-text-color)',
    accent: 'var(--color-blue)',
    divider: 'var(--border-color)',
    frame: '#ffffff',
    font: 'inherit',
  },
  lion: {
    surface: '#f7f7f7',
    radius: '20px',
    title: '#333333',
    hint: '#8a8a8a',
    accent: '#6aaa3a', // LION_GREEN
    divider: '#e0e0e0', // LION_BORDER
    frame: '#ffffff',
    font: "'PT Mono', monospace",
  },
  pinco: {
    surface: '#1d1d1d', // PINCO_CARD_BG
    radius: '24px',
    title: '#ffffff',
    hint: '#8a8a8a',
    accent: '#FF0123', // PINCO_RED
    divider: '#2a2a2a', // PINCO_BORDER
    frame: '#ffffff',
    font: "'PT Mono', monospace",
  },
};

const Wrap = styled.div`
  width: 100%;
  box-sizing: border-box;
  margin: 4px 0 16px;
  text-align: left;
  font-family: var(--oop-font);
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  font-size: 12px;
  line-height: 1;
  color: var(--oop-hint);

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--oop-divider);
  }
`;

const Card = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  padding: 16px;
  background: var(--oop-surface);
  border-radius: var(--oop-radius);
`;

/**
 * Уголки-визир вокруг кода — единственный «украшающий» элемент блока. Два уголка,
 * а не четыре: рамка читается как прицел камеры и при этом не спорит с загрузчиком.
 */
const Frame = styled.div`
  position: relative;
  flex-shrink: 0;
  display: inline-flex;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 16px;
    height: 16px;
    border: 2px solid var(--oop-accent);
    pointer-events: none;
  }

  &::before {
    top: -5px;
    left: -5px;
    border-right: 0;
    border-bottom: 0;
    border-radius: 6px 0 0 0;
  }

  &::after {
    right: -5px;
    bottom: -5px;
    border-top: 0;
    border-left: 0;
    border-radius: 0 0 6px 0;
  }
`;

const Copy = styled.div`
  min-width: 0;
`;

const Title = styled.p`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.35;
  color: var(--oop-title);
`;

const Hint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: var(--oop-hint);
`;

interface Props {
  /** Призыв к действию — «Загрузите чек с телефона». */
  title: string;
  /** Пояснение, как это сделать. */
  hint: string;
  /** Надпись на разделителе над блоком — «или». */
  orLabel: string;
  variant?: OpenOnPhoneVariant;
  className?: string;
}

const OpenOnPhone = ({
  title,
  hint,
  orLabel,
  variant = 'default',
  className,
}: Props) => {
  const isDesktop = useIsDesktop();

  if (!isDesktop || typeof window === 'undefined') return null;

  // Тот же адрес, что в строке браузера: состояние тикета телефон подхватит по UUID сам.
  const url = window.location.href;
  if (!url) return null;

  const tokens = TOKENS[variant];
  const style = {
    '--oop-surface': tokens.surface,
    '--oop-radius': tokens.radius,
    '--oop-title': tokens.title,
    '--oop-hint': tokens.hint,
    '--oop-accent': tokens.accent,
    '--oop-divider': tokens.divider,
    '--oop-font': tokens.font,
  } as CSSProperties;

  return (
    <Wrap className={className} style={style}>
      <Divider>{orLabel}</Divider>
      <Card>
        <Frame>
          <QR data={url} size={96} frameColor={tokens.frame} />
        </Frame>
        <Copy>
          <Title>{title}</Title>
          <Hint>{hint}</Hint>
        </Copy>
      </Card>
    </Wrap>
  );
};

export default OpenOnPhone;
